// <modal-dialog> — the app's modal, used directly and as the confirm
// pattern (Ui::Modal / Ui::ConfirmButton). Native <dialog> does the heavy
// lifting: modality, Escape, backdrop, focus containment. Markup convention:
//
//   <modal-dialog>
//     <button data-modal-open>…trigger…</button>
//     <dialog data-modal-dialog>
//       …content, including <button data-modal-close> and/or a submit…
//     </dialog>
//   </modal-dialog>
//
// Backdrop click closes (clicks landing on the dialog element itself are
// outside the content box). Submit buttons inside close naturally via
// navigation; torn down before Turbo caches so restored snapshots never
// carry an open modal. Without JS the trigger is inert — for confirms that
// fails safe (no accidental un-confirmed submits).

class ModalDialog extends HTMLElement {
	#abort: AbortController | null = null;

	connectedCallback() {
		const dialog = this.querySelector<HTMLDialogElement>("[data-modal-dialog]");
		if (!dialog) return;

		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.querySelector("[data-modal-open]")?.addEventListener(
			"click",
			() => dialog.showModal(),
			{ signal },
		);

		for (const closer of this.querySelectorAll("[data-modal-close]")) {
			closer.addEventListener("click", () => dialog.close(), { signal });
		}

		dialog.addEventListener(
			"click",
			(event) => {
				if (event.target === dialog) dialog.close();
			},
			{ signal },
		);

		document.addEventListener("turbo:before-cache", () => dialog.close(), {
			signal,
		});
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}
}

if (!customElements.get("modal-dialog")) {
	customElements.define("modal-dialog", ModalDialog);
}
